import { useTranslation } from "react-i18next"

import type { ChatAppResponse, ResponseContextDataPoint } from "@/api/models"

import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { uiOption } from "@/lib/ui-options"
import { getResponseContext } from "@/utils/response-format-utils"

import { SupportingContent } from "./supporting-content"
import { ThoughtProcess } from "./thought-process"

export type AnalysisPanelProps = {
  children: React.ReactNode
  answer: ChatAppResponse
  citationSection?: string
}

export function AnalysisPanel({ children, answer, citationSection }: AnalysisPanelProps) {
  const { t } = useTranslation()

  const context = getResponseContext(answer)

  const isDisabledThoughtProcessTab: boolean = Array.isArray(context.thoughts)
    ? context.thoughts.length === 0
    : !context.thoughts
  const isDisabledSupportingContentTab: boolean = Array.isArray(context.data_points)
    ? context.data_points.length === 0
    : !context.data_points

  const showThoughtProcess = uiOption("show_thought_process")

  // For Agents with multiple web searches
  function isResponseContextDataPointArray(data: unknown): data is ResponseContextDataPoint[] {
    return (
      Array.isArray(data) &&
      data.every((item) => typeof item === "object" && item !== null && "title" in item)
    )
  }
  const multipleSupportingContent =
    !isDisabledSupportingContentTab && isResponseContextDataPointArray(context.data_points)

  function getDefaultTab() {
    if (showThoughtProcess && !isDisabledThoughtProcessTab) {
      return "thought_process"
    } else if (!isDisabledSupportingContentTab) {
      return "supporting_content"
    } else {
      return ""
    }
  }

  return (
    <Sheet>
      <SheetTrigger asChild>{children}</SheetTrigger>
      <SheetContent className="h-full w-[90%] overflow-y-auto sm:max-w-[800px] md:w-[70%] lg:w-1/2">
        <Tabs className="w-full" defaultValue={getDefaultTab()}>
          <SheetHeader className="mb-3 w-full">
            <SheetTitle className="text-left">{t("analysispanel.analysis_panel")}</SheetTitle>
            <TabsList className="h-full flex-row flex-wrap items-start">
              <TabsTrigger
                className="flex-1"
                disabled={isDisabledThoughtProcessTab || !showThoughtProcess}
                value="thought_process"
              >
                {t("analysispanel.thought_process")}
              </TabsTrigger>

              {multipleSupportingContent ? (
                (context.data_points as ResponseContextDataPoint[]).map(
                  (dataPoint: ResponseContextDataPoint, index: number) => (
                    <TabsTrigger
                      key={index}
                      className="flex-1"
                      disabled={isDisabledSupportingContentTab}
                      value={`supporting_content-${index}`}
                    >
                      {dataPoint.title || t("analysispanel.supporting_content")}
                    </TabsTrigger>
                  )
                )
              ) : (
                // One tab, for just one web search process (explorer)
                <TabsTrigger
                  className="flex-1"
                  disabled={isDisabledSupportingContentTab}
                  value="supporting_content"
                >
                  {t("analysispanel.supporting_content")}
                </TabsTrigger>
              )}
            </TabsList>
          </SheetHeader>
          <TabsContent value="thought_process">
            <ThoughtProcess thoughts={context.thoughts} />
          </TabsContent>

          {multipleSupportingContent ? (
            (context.data_points as ResponseContextDataPoint[]).map(
              (dataPoint: ResponseContextDataPoint, index: number) => (
                <TabsContent key={index} value={`supporting_content-${index}`}>
                  <SupportingContent
                    citationSection={citationSection}
                    supportingContent={dataPoint}
                  />
                </TabsContent>
              )
            )
          ) : (
            <TabsContent value="supporting_content">
              <SupportingContent
                citationSection={citationSection}
                supportingContent={
                  context.data_points as
                    | string[]
                    | { text: string[]; images?: { url: string }[] | undefined }
                }
              />
            </TabsContent>
          )}
        </Tabs>
      </SheetContent>
    </Sheet>
  )
}
